import { Component, OnInit } from "@angular/core";
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from "@angular/forms";
import { forkJoin } from "rxjs";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import Swal from "sweetalert2";
import { HttpHelpService } from "../service/http-help.service";

type PlanCatalog = "main" | "doc" | "company" | "token";

interface BasePlan {
	id: number;
	name_en: string;
	name_ar?: string | null;
	name_he?: string | null;
	description_en?: string | null;
	description_ar?: string | null;
	description_he?: string | null;
	price: string;
	is_active: boolean;
	sort_order: number;
	created_at: string;
	updated_at: string;
}

interface SubscriptionPlan extends BasePlan {
	documents_limit: number;
	companies_limit: number;
	duration_days: number;
	analysis_tokens_limit?: number;
}

interface DocExtensionPlan extends BasePlan {
	extra_documents: number;
}

interface CompanyExtensionPlan extends BasePlan {
	extra_companies: number;
}

interface TokenExtensionPlan extends BasePlan {
	extra_tokens?: number;
	/** بعض الاستجابات القديمة تستخدم documents_count */
	documents_count?: number;
}

@Component({
	selector: "app-plans-info",
	templateUrl: "./plans-info.component.html",
	styleUrls: ["./plans-info.component.scss"],
})
export class PlansInfoComponent implements OnInit {
	curentLang: string = localStorage.getItem("curentLang") || "en";
	term: string = "";
	activeTab: PlanCatalog = "main";

	mainPlans: SubscriptionPlan[] = [];
	docExtensionPlans: DocExtensionPlan[] = [];
	companyExtensionPlans: CompanyExtensionPlan[] = [];
	tokenExtensionPlans: TokenExtensionPlan[] = [];

	planForm: FormGroup;
	isEditMode: boolean = false;
	isSubmitting: boolean = false;
	currentCatalog: PlanCatalog = "main";
	editingPlanId: number | null = null;

	constructor(
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		private modalService: NgbModal,
		private router: Router
	) {
		this.planForm = new FormGroup({
			name_en: new FormControl("", [Validators.required, this.noWhitespaceValidator()]),
			name_ar: new FormControl(""),
			name_he: new FormControl(""),
			description_en: new FormControl(""),
			description_ar: new FormControl(""),
			description_he: new FormControl(""),
			price: new FormControl(null, [Validators.required, Validators.min(0.01)]),
			is_active: new FormControl(true),
			sort_order: new FormControl(0, [Validators.min(0), this.integerValidator()]),
			documents_limit: new FormControl(null),
			companies_limit: new FormControl(null),
			duration_days: new FormControl(null),
			analysis_tokens_limit: new FormControl(null),
			extra_documents: new FormControl(null),
			extra_companies: new FormControl(null),
			extra_tokens: new FormControl(null),
		});

	}

	ngOnInit(): void {
		this.loadAllCatalogs();
	}

	get displayedPlans(): any[] {
		const list: any[] =
			this.activeTab === "main"
				? this.mainPlans
				: this.activeTab === "doc"
					? this.docExtensionPlans
					: this.activeTab === "company"
						? this.companyExtensionPlans
						: this.tokenExtensionPlans;

		const keyword = (this.term || "").toLowerCase().trim();
		if (!keyword) {
			return list;
		}

		return list.filter((item) => {
			const values = [
				item.name_en,
				item.name_ar || "",
				item.name_he || "",
				item.description_en || "",
				item.description_ar || "",
				item.description_he || "",
			];
			return values.some((v) => (v || "").toLowerCase().includes(keyword));
		});
	}

	setActiveTab(tab: PlanCatalog): void {
		this.activeTab = tab;
		this.term = "";
	}

	goToMerchantSubscriptions(): void {
		this.router.navigate(["/dashboard/admin-merchant-subscriptions"]);
	}

	goToCustomPayments(): void {
		this.router.navigate(["/dashboard/admin-custom-payments"]);
	}

	t(ar: string, en: string, he: string): string {
		if (this.curentLang === "ar") {
			return ar;
		}
		if (this.curentLang === "he") {
			return he;
		}
		return en;
	}

	isInvalid(controlName: string): boolean {
		const control = this.planForm.get(controlName);
		return !!control && control.invalid && (control.touched || control.dirty);
	}

	loadAllCatalogs(): void {
		this.spinner.show();
		forkJoin({
			main: this.service.getAdminV2MainPlans(),
			doc: this.service.getAdminV2DocExtensionPlans(),
			company: this.service.getAdminV2CompanyExtensionPlans(),
			token: this.service.getAdminV2TokenExtensionPlans(),
		}).subscribe({
			next: (res) => {
				this.mainPlans = res.main || [];
				this.docExtensionPlans = res.doc || [];
				this.companyExtensionPlans = res.company || [];
				this.tokenExtensionPlans = res.token || [];
				this.spinner.hide();
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	openCreateModal(content: any): void {
		this.isEditMode = false;
		this.currentCatalog = this.activeTab;
		this.editingPlanId = null;
		this.resetFormForCatalog(this.currentCatalog);
		this.modalService.open(content, { size: "xl", centered: true });
	}

	openEditModal(
		content: any,
		catalog: PlanCatalog,
		plan: SubscriptionPlan | DocExtensionPlan | CompanyExtensionPlan | TokenExtensionPlan
	): void {
		this.isEditMode = true;
		this.currentCatalog = catalog;
		this.editingPlanId = plan.id;
		this.resetFormForCatalog(catalog);

		this.planForm.patchValue({
			name_en: plan.name_en || "",
			name_ar: plan.name_ar || "",
			name_he: plan.name_he || "",
			description_en: plan.description_en || "",
			description_ar: plan.description_ar || "",
			description_he: plan.description_he || "",
			price: Number(plan.price),
			is_active: !!plan.is_active,
			sort_order: plan.sort_order ?? 0,
		});

		if (catalog === "main") {
			const mainPlan = plan as SubscriptionPlan;
			this.planForm.patchValue({
				documents_limit: mainPlan.documents_limit,
				companies_limit: mainPlan.companies_limit,
				duration_days: mainPlan.duration_days,
				analysis_tokens_limit: mainPlan.analysis_tokens_limit ?? 0,
			});
		} else if (catalog === "doc") {
			this.planForm.patchValue({
				extra_documents: (plan as DocExtensionPlan).extra_documents,
			});
		} else if (catalog === "company") {
			this.planForm.patchValue({
				extra_companies: (plan as CompanyExtensionPlan).extra_companies,
			});
		} else {
			const tp = plan as TokenExtensionPlan;
			this.planForm.patchValue({
				extra_tokens: tp.extra_tokens ?? tp.documents_count ?? null,
			});
		}

		this.modalService.open(content, { size: "xl", centered: true });
	}

	submitPlan(): void {
		this.applyCatalogValidators(this.currentCatalog);
		this.planForm.updateValueAndValidity();

		if (this.planForm.invalid) {
			this.planForm.markAllAsTouched();
			this.showWarning(
				this.t(
					"يرجى تعبئة الحقول المطلوبة وإدخال قيم صحيحة",
					"Please fill all required fields with valid values",
					"אנא מלא את כל השדות הנדרשים עם ערכים תקינים"
				)
			);
			return;
		}

		const payload = this.buildPayload(this.currentCatalog);
		const request$ = this.isEditMode && this.editingPlanId
			? this.getUpdateRequest(this.currentCatalog, this.editingPlanId, payload)
			: this.getCreateRequest(this.currentCatalog, payload);

		this.isSubmitting = true;
		this.spinner.show();
		request$.subscribe({
			next: () => {
				this.spinner.hide();
				this.isSubmitting = false;
				this.modalService.dismissAll();
				this.showSuccess(
					this.t(
						"تم حفظ الخطة بنجاح",
						"Plan saved successfully",
						"התוכנית נשמרה בהצלחה"
					)
				);
				this.loadAllCatalogs();
			},
			error: (error) => {
				this.spinner.hide();
				this.isSubmitting = false;
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	togglePlanActive(
		catalog: PlanCatalog,
		plan: SubscriptionPlan | DocExtensionPlan | CompanyExtensionPlan | TokenExtensionPlan
	): void {
		const model = { is_active: !plan.is_active };
		this.spinner.show();

		this.getUpdateRequest(catalog, plan.id, model).subscribe({
			next: () => {
				this.spinner.hide();
				this.showSuccess(
					this.t(
						"تم تحديث حالة الخطة بنجاح",
						"Plan status updated successfully",
						"סטטוס התוכנית עודכן בהצלחה"
					)
				);
				this.loadAllCatalogs();
			},
			error: (error) => {
				this.spinner.hide();
				this.showError(this.extractErrorMessage(error));
			},
		});
	}

	deletePlan(
		catalog: PlanCatalog,
		plan: SubscriptionPlan | DocExtensionPlan | CompanyExtensionPlan | TokenExtensionPlan
	): void {
		Swal.fire({
			icon: "warning",
			title: this.t(
				"هل أنت متأكد من حذف الخطة؟",
				"Are you sure you want to delete this plan?",
				"האם למחוק את התוכנית?"
			),
			showCancelButton: true,
			confirmButtonText: this.t("حذف", "Delete", "מחק"),
			cancelButtonText: this.t("إلغاء", "Cancel", "בטל"),
		}).then((result) => {
			if (!result.isConfirmed) {
				return;
			}

			this.spinner.show();
			this.getDeleteRequest(catalog, plan.id).subscribe({
				next: () => {
					this.spinner.hide();
					this.showSuccess(
						this.t(
							"تم حذف الخطة بنجاح",
							"Plan deleted successfully",
							"התוכנית נמחקה בהצלחה"
						)
					);
					this.loadAllCatalogs();
				},
				error: (error) => {
					this.spinner.hide();
					this.showError(this.extractErrorMessage(error));
				},
			});
		});
	}

	getPlanName(plan: BasePlan): string {
		if (this.curentLang === "ar") {
			return plan.name_ar || plan.name_en;
		}
		if (this.curentLang === "he") {
			return plan.name_he || plan.name_en;
		}
		return plan.name_en;
	}

	getPlanDescription(plan: BasePlan): string {
		if (this.curentLang === "ar") {
			return plan.description_ar || plan.description_en || "-";
		}
		if (this.curentLang === "he") {
			return plan.description_he || plan.description_en || "-";
		}
		return plan.description_en || "-";
	}

	getCurrentCatalogLabel(): string {
		if (this.currentCatalog === "main") {
			return this.t("الخطط الرئيسية", "Main Plans", "תוכניות ראשיות");
		}
		if (this.currentCatalog === "doc") {
			return this.t(
				"خطط زيادة المستندات",
				"Document Extensions",
				"הרחבות מסמכים"
			);
		}
		if (this.currentCatalog === "company") {
			return this.t("خطط زيادة الشركات", "Company Extensions", "הרחבות חברות");
		}
		return this.t(
			"امتدادات توكنات التحليل",
			"Analysis token extensions",
			"הרחבות טוקני ניתוח"
		);
	}

	get emptyTableColspan(): number {
		return this.activeTab === "main" ? 12 : 9;
	}

	private resetFormForCatalog(catalog: PlanCatalog): void {
		this.planForm.reset({
			name_en: "",
			name_ar: "",
			name_he: "",
			description_en: "",
			description_ar: "",
			description_he: "",
			price: null,
			is_active: true,
			sort_order: 0,
			documents_limit: null,
			companies_limit: null,
			duration_days: null,
			analysis_tokens_limit: null,
			extra_documents: null,
			extra_companies: null,
			extra_tokens: null,
		});
		this.applyCatalogValidators(catalog);
	}

	private applyCatalogValidators(catalog: PlanCatalog): void {
		const positiveIntegerValidators = [
			Validators.required,
			Validators.min(1),
			this.integerValidator(),
		];

		this.planForm.get("documents_limit")?.clearValidators();
		this.planForm.get("companies_limit")?.clearValidators();
		this.planForm.get("duration_days")?.clearValidators();
		this.planForm.get("analysis_tokens_limit")?.clearValidators();
		this.planForm.get("extra_documents")?.clearValidators();
		this.planForm.get("extra_companies")?.clearValidators();
		this.planForm.get("extra_tokens")?.clearValidators();

		if (catalog === "main") {
			this.planForm.get("documents_limit")?.setValidators(positiveIntegerValidators);
			this.planForm.get("companies_limit")?.setValidators(positiveIntegerValidators);
			this.planForm.get("duration_days")?.setValidators(positiveIntegerValidators);
			this.planForm.get("analysis_tokens_limit")?.setValidators([
				Validators.required,
				Validators.min(0),
				this.integerValidator(),
			]);
		} else if (catalog === "doc") {
			this.planForm.get("extra_documents")?.setValidators(positiveIntegerValidators);
		} else if (catalog === "company") {
			this.planForm.get("extra_companies")?.setValidators(positiveIntegerValidators);
		} else {
			this.planForm.get("extra_tokens")?.setValidators(positiveIntegerValidators);
		}

		this.planForm.get("documents_limit")?.updateValueAndValidity({ emitEvent: false });
		this.planForm.get("companies_limit")?.updateValueAndValidity({ emitEvent: false });
		this.planForm.get("duration_days")?.updateValueAndValidity({ emitEvent: false });
		this.planForm.get("analysis_tokens_limit")?.updateValueAndValidity({ emitEvent: false });
		this.planForm.get("extra_documents")?.updateValueAndValidity({ emitEvent: false });
		this.planForm.get("extra_companies")?.updateValueAndValidity({ emitEvent: false });
		this.planForm.get("extra_tokens")?.updateValueAndValidity({ emitEvent: false });
	}

	private buildPayload(catalog: PlanCatalog): any {
		const value = this.planForm.value;

		const payload: any = {
			name_en: this.cleanRequired(value.name_en),
			name_ar: this.cleanNullable(value.name_ar),
			name_he: this.cleanNullable(value.name_he),
			description_en: this.cleanNullable(value.description_en),
			description_ar: this.cleanNullable(value.description_ar),
			description_he: this.cleanNullable(value.description_he),
			price: Number(value.price),
			is_active: !!value.is_active,
			sort_order:
				value.sort_order === null || value.sort_order === undefined || value.sort_order === ""
					? 0
					: Number(value.sort_order),
		};

		if (catalog === "main") {
			payload.documents_limit = Number(value.documents_limit);
			payload.companies_limit = Number(value.companies_limit);
			payload.duration_days = Number(value.duration_days);
			payload.analysis_tokens_limit = Number(value.analysis_tokens_limit);
		} else if (catalog === "doc") {
			payload.extra_documents = Number(value.extra_documents);
		} else if (catalog === "company") {
			payload.extra_companies = Number(value.extra_companies);
		} else {
			payload.extra_tokens = Number(value.extra_tokens);
		}

		return payload;
	}

	private getCreateRequest(catalog: PlanCatalog, payload: any) {
		if (catalog === "main") {
			return this.service.createAdminV2MainPlan(payload);
		}
		if (catalog === "doc") {
			return this.service.createAdminV2DocExtensionPlan(payload);
		}
		if (catalog === "company") {
			return this.service.createAdminV2CompanyExtensionPlan(payload);
		}
		return this.service.createAdminV2TokenExtensionPlan(payload);
	}

	private getUpdateRequest(catalog: PlanCatalog, id: number, payload: any) {
		if (catalog === "main") {
			return this.service.updateAdminV2MainPlan(id, payload);
		}
		if (catalog === "doc") {
			return this.service.updateAdminV2DocExtensionPlan(id, payload);
		}
		if (catalog === "company") {
			return this.service.updateAdminV2CompanyExtensionPlan(id, payload);
		}
		return this.service.updateAdminV2TokenExtensionPlan(id, payload);
	}

	private getDeleteRequest(catalog: PlanCatalog, id: number) {
		if (catalog === "main") {
			return this.service.deleteAdminV2MainPlan(id);
		}
		if (catalog === "doc") {
			return this.service.deleteAdminV2DocExtensionPlan(id);
		}
		if (catalog === "company") {
			return this.service.deleteAdminV2CompanyExtensionPlan(id);
		}
		return this.service.deleteAdminV2TokenExtensionPlan(id);
	}

	private cleanRequired(value: any): string {
		return (value || "").toString().trim();
	}

	private cleanNullable(value: any): string | null {
		const cleaned = (value || "").toString().trim();
		return cleaned.length ? cleaned : null;
	}

	private extractErrorMessage(error: any): string {
		const apiError = error?.error;
		const apiMessage = apiError?.message;

		if (this.curentLang === "ar" && (apiError?.messageAr || apiError?.message_ar)) {
			return apiError.messageAr || apiError.message_ar;
		}
		if (this.curentLang === "en" && (apiError?.messageEn || apiError?.message_en)) {
			return apiError.messageEn || apiError.message_en;
		}
		if (this.curentLang === "he" && (apiError?.messageHe || apiError?.message_he)) {
			return apiError.messageHe || apiError.message_he;
		}

		if (Array.isArray(apiMessage) && apiMessage.length) {
			return apiMessage.join(", ");
		}

		if (typeof apiMessage === "string" && apiMessage.trim().length) {
			return apiMessage;
		}

		return this.t("حدث خطأ غير متوقع", "Unexpected error occurred", "אירעה שגיאה לא צפויה");
	}

	private showSuccess(title: string): void {
		Swal.fire({ icon: "success", title });
	}

	private showError(title: string): void {
		Swal.fire({ icon: "error", title });
	}

	private showWarning(title: string): void {
		Swal.fire({ icon: "warning", title });
	}

	private noWhitespaceValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = (control.value || "").toString();
			return value.trim().length ? null : { whitespace: true };
		};
	}

	private integerValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (control.value === null || control.value === undefined || control.value === "") {
				return null;
			}
			return Number.isInteger(Number(control.value)) ? null : { integer: true };
		};
	}

}
